import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useDispatch, useSelector } from "react-redux";
import {
  addMessage,
  clearMessages,
  upsertChat,
  setUserTyping,
} from "../features/chats/chatSlice.js";
import {
  fetchChatHistory,
  fetchChatUsers,
  fetchMoreChatHistory,
  initChat,
  fetchChannels,
  addChannelMembers,
  removeChannelMembers,
  updateChannel,
  deleteChannel,
  fetchUnreadCounts,
  markChatAsRead,
} from "../features/chats/chatThunks.js";
import { Eye, FileText, Plus, SendHorizontal, Settings } from "lucide-react";
import "../styles/chats/chatroom.css";

const ChatRoom = () => {
  const dispatch = useDispatch();
  const { chatId: chatIdParam } = useParams();
  const location = useLocation();
  const navigate = useNavigate();

  const chatContainerRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const endOfMessagesRef = useRef(null);
  const socketRef = useRef(null);
  const initialMessageSentRef = useRef(false);
  const typingTimeoutRef = useRef(null);

  const [messageText, setMessageText] = useState("");
  const [isSocketOpen, setIsSocketOpen] = useState(false);
  const [showAddUsers, setShowAddUsers] = useState(false);

  const [uploadingFile, setUploadingFile] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);

  const [channelSettings, setChannelSettings] = useState({ name: "", topic: "" });
  const [savingChannelSettings, setSavingChannelSettings] = useState(false);
  const [channelSettingsError, setChannelSettingsError] = useState(null);

  const currentUser = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);

  const chatUsers = useSelector((state) =>
    Array.isArray(state.chat.chatUsers) ? state.chat.chatUsers : []
  );
  const myChats = useSelector((state) =>
    Array.isArray(state.chat.myChats) ? state.chat.myChats : []
  );
  const channels = useSelector((state) =>
    Array.isArray(state.chat.channels) ? state.chat.channels : []
  );

  const chatUsersById = useMemo(() => {
    const map = new Map();
    for (const user of chatUsers) {
      map.set(String(user.id), user);
    }
    return map;
  }, [chatUsers]);

  const chatIdKey = String(chatIdParam || "");

  const messagesByChat = useSelector((state) => state.chat.messagesByChatId) || {};
  const messages = messagesByChat[chatIdKey] ?? messagesByChat[Number(chatIdKey)] ?? [];

  const nextCursor = useSelector((state) => state.chat.historyMeta?.[chatIdKey]?.nextCursor);
  const loading = useSelector((state) => state.chat.loading);

  const typingByChatId = useSelector((state) => state.chat.typingByChatId) || {};
  const typingUserIds = typingByChatId[chatIdKey] || [];

  const activeChat = useMemo(
    () =>
      myChats.find((c) => String(c.id) === chatIdKey) ||
      channels.find((c) => String(c.id) === chatIdKey),
    [myChats, channels, chatIdKey]
  );

  const isGroupChat = Boolean(activeChat?.is_group);
  const canManageChannels = currentUser?.role === "admin";

  const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  const mediaBase = (process.env.REACT_APP_MEDIA_URL || "").replace(/\/$/, "");

  const getMediaUrl = (raw) => {
    if (!raw) return null;
    if (raw.startsWith("http://") || raw.startsWith("https://")) return raw;

    const path = raw.startsWith("/") ? raw : `/${raw}`;
    if (!mediaBase) return path;

    if (mediaBase.endsWith("/media") && path.startsWith("/media/")) {
      return `${mediaBase}${path.slice("/media".length)}`;
    }

    return `${mediaBase}${path}`;
  };

  const getUserDisplayName = (user) => {
    if (!user) return "Usuario";
    const firstName = (user.first_name || "").trim();
    const lastName = (user.last_name || "").trim();
    const fullName = `${firstName} ${lastName}`.trim();
    if (fullName) return fullName;
    return user.username || "Usuario";
  };

  const fallbackReceiver = useMemo(() => {
    if (!activeChat || activeChat.is_group || !currentUser) return null;
    return (
      (activeChat.participants || []).find((p) => p.id !== currentUser.id) ||
      activeChat.receiver ||
      null
    );
  }, [activeChat, currentUser]);

  const receiverId = !isGroupChat
    ? location.state?.receiverId || fallbackReceiver?.id || null
    : null;

  const receiverForDM = useMemo(() => {
    if (isGroupChat) return null;
    if (!receiverId) return fallbackReceiver;
    return chatUsersById.get(String(receiverId)) || fallbackReceiver || null;
  }, [chatUsersById, receiverId, fallbackReceiver, isGroupChat]);

  const headerTitle = useMemo(() => {
    if (isGroupChat && activeChat) {
      const name = activeChat.name || "canal-sin-nombre";
      return `#${name}`;
    }
    if (receiverForDM) return getUserDisplayName(receiverForDM);
    return "Chat";
  }, [isGroupChat, activeChat, receiverForDM]);

  const headerSubtitle = useMemo(() => {
    if (isGroupChat && activeChat) {
      const participantsCount = Array.isArray(activeChat.participants)
        ? activeChat.participants.length
        : 0;

      if (activeChat.topic) return activeChat.topic;
      if (participantsCount > 0) {
        return `${participantsCount} participante${participantsCount === 1 ? "" : "s"}`;
      }
      return "Canal interno";
    }

    if (receiverForDM?.role) {
      if (receiverForDM.role === "doctor") return "Doctor/a";
      if (receiverForDM.role === "patient") return "Paciente";
      if (receiverForDM.role === "receptionist") return "Recepción";
      return receiverForDM.role;
    }

    return "";
  }, [isGroupChat, activeChat, receiverForDM]);

  const groupParticipants = useMemo(() => {
    if (!isGroupChat || !activeChat) return [];
    const participants = Array.isArray(activeChat.participants) ? activeChat.participants : [];

    return participants
      .map((participant) => {
        const fullUser = chatUsersById.get(String(participant.id)) || participant;
        const label = getUserDisplayName(fullUser);
        const initial = (label[0] || "?").toUpperCase();

        return {
          id: participant.id,
          label,
          initial,
          role: fullUser.role,
          avatarUrl: fullUser.profile_picture_url || fullUser.profile_picture || null,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [isGroupChat, activeChat, chatUsersById]);

  const headerAvatar = useMemo(() => {
    if (isGroupChat) {
      const base = activeChat?.name || "#";
      const firstChar = String(base).trim()[0] || "#";
      return { type: "channel", initials: firstChar.toUpperCase() };
    }

    if (receiverForDM) {
      const avatarUrl = receiverForDM.profile_picture_url || receiverForDM.profile_picture || null;
      const label = getUserDisplayName(receiverForDM);
      const initials = (label[0] || "?").toUpperCase();
      return { type: "user", avatarUrl, label, initials };
    }

    return { type: "empty", initials: "?" };
  }, [isGroupChat, activeChat, receiverForDM]);

  const messagePlaceholder = useMemo(() => {
    if (isGroupChat) {
      const name = activeChat?.name || "el canal";
      return `Escribe un mensaje en #${name}…`;
    }
    if (receiverForDM) {
      const name = getUserDisplayName(receiverForDM);
      return `Escribe un mensaje para ${name}…`;
    }
    return "Escribe tu mensaje…";
  }, [isGroupChat, activeChat, receiverForDM]);

  const availableUsersToAdd = useMemo(() => {
    if (!isGroupChat || !activeChat || !currentUser) return [];
    const currentIds = new Set((activeChat.participants || []).map((p) => p.id));

    return chatUsers
      .filter((u) => {
        if (u.id === currentUser.id) return false;
        if (u.role === "patient") return false;
        if (currentIds.has(u.id)) return false;
        return true;
      })
      .sort((a, b) =>
        getUserDisplayName(a)
          .toLowerCase()
          .localeCompare(getUserDisplayName(b).toLowerCase())
      );
  }, [isGroupChat, activeChat, currentUser, chatUsers]);

  const typingUsers = useMemo(() => {
    if (!typingUserIds || typingUserIds.length === 0) return [];
    const users = typingUserIds
      .map((id) => chatUsersById.get(String(id)))
      .filter(Boolean);
    return users.filter((u) => u.id !== currentUser?.id);
  }, [typingUserIds, chatUsersById, currentUser]);

  const previewTitle = useMemo(() => {
    if (!previewAttachment) return "";
    if (previewAttachment.type === "image") return "Vista previa de imagen";
    if (previewAttachment.type === "video") return "Vista previa de vídeo";
    if (previewAttachment.type === "document") return "Vista previa de documento";
    return "Vista previa de archivo";
  }, [previewAttachment]);

  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [messageText]);

  useEffect(() => {
    if (!token) return;
    if (chatUsers.length === 0) dispatch(fetchChatUsers());
  }, [dispatch, token, chatUsers.length]);

  useEffect(() => {
    if (chatIdKey) {
      dispatch(fetchChatHistory(chatIdKey));
      dispatch(markChatAsRead(chatIdKey));
      dispatch(fetchUnreadCounts());
    }
    return () => dispatch(clearMessages());
  }, [dispatch, chatIdKey]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    initialMessageSentRef.current = false;
  }, [chatIdKey, receiverId]);

  useEffect(() => {
    if (!chatIdKey || !currentUser || !token) return;

    const wsBase = (process.env.REACT_APP_WS_URL || "").replace(/\/$/, "");
    const wsUrl = `${wsBase}/ws/chat/${chatIdKey}/?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    socketRef.current = ws;

    ws.onopen = () => setIsSocketOpen(true);
    ws.onclose = () => setIsSocketOpen(false);
    ws.onerror = (event) => console.log("WS error", event);

    ws.onmessage = (event) => {
      let data;
      try {
        data = JSON.parse(event.data);
      } catch {
        return;
      }

      if (data.kind === "typing") {
        const senderId = data.sender_id || data.sender?.id;
        if (senderId && senderId !== currentUser.id) {
          dispatch(
            setUserTyping({
              chatId: chatIdKey,
              userId: senderId,
              isTyping: !!data.is_typing,
            })
          );
        }
        return;
      }

      const normalized = {
        ...data,
        sender_id: data.sender?.id || data.sender_id,
        receiver_id: data.receiver?.id || data.receiver_id,
      };

      dispatch(addMessage(normalized));
    };

    return () => {
      ws.close();
      socketRef.current = null;
    };
  }, [chatIdKey, currentUser, token, dispatch]);

  useEffect(() => {
    if (!showAddUsers || !isGroupChat || !activeChat) return;
    setChannelSettings({
      name: activeChat.name || "",
      topic: activeChat.topic || "",
    });
    setChannelSettingsError(null);
  }, [showAddUsers, isGroupChat, activeChat]);

  useEffect(() => {
    if (isGroupChat) return;

    if (
      isSocketOpen &&
      chatIdKey &&
      receiverId &&
      location.state?.initialMessage &&
      !initialMessageSentRef.current
    ) {
      const text = String(location.state.initialMessage).trim();
      if (text) {
        socketRef.current?.send(
          JSON.stringify({
            message: text,
            sender_id: currentUser.id,
            receiver_id: receiverId,
            chat_id: chatIdKey,
          })
        );
        initialMessageSentRef.current = true;
        navigate(".", { replace: true, state: { receiverId } });
      }
    }
  }, [isSocketOpen, chatIdKey, receiverId, location.state, currentUser?.id, navigate, isGroupChat]);

  useEffect(() => {
    endOfMessagesRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [chatIdKey, messages.length]);

  const handleAddUserToChannel = async (userId) => {
    if (!isGroupChat || !activeChat) return;
    if (!canManageChannels) return;

    const action = await dispatch(
      addChannelMembers({ channelId: activeChat.id, participantIds: [userId] })
    );

    if (addChannelMembers.fulfilled.match(action)) {
      dispatch(fetchChannels());
    }
  };

  const handleRemoveUserFromChannel = async (userId) => {
    if (!isGroupChat || !activeChat) return;
    if (!canManageChannels) return;
    if (!userId || userId === currentUser?.id) return;

    const action = await dispatch(
      removeChannelMembers({ channelId: activeChat.id, participantIds: [userId] })
    );

    if (removeChannelMembers.fulfilled.match(action)) {
      dispatch(fetchChannels());
    }
  };

  const sendMessage = async () => {
    const text = messageText.trim();
    if (!text || !currentUser) return;

    if (isGroupChat) {
      if (!chatIdKey || !isSocketOpen) return;
      socketRef.current?.send(
        JSON.stringify({ message: text, sender_id: currentUser.id, chat_id: chatIdKey })
      );
      setMessageText("");
      return;
    }

    if (!receiverId) return;

    if (chatIdKey) {
      if (!isSocketOpen) return;
      socketRef.current?.send(
        JSON.stringify({
          message: text,
          sender_id: currentUser.id,
          receiver_id: receiverId,
          chat_id: chatIdKey,
        })
      );
      setMessageText("");
      return;
    }

    const result = await dispatch(initChat({ senderId: currentUser.id, receiverId }));

    if (initChat.fulfilled.match(result)) {
      const newChatId = result.payload?.chat_id;
      if (newChatId) {
        const receiverUser = chatUsersById.get(String(receiverId)) || { id: receiverId };

        dispatch(
          upsertChat({
            id: newChatId,
            participants: [currentUser, receiverUser],
            sender: currentUser,
            receiver: receiverUser,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
            expires_at: result.payload?.expires_at || null,
          })
        );

        navigate(`/chats/${newChatId}`, {
          replace: true,
          state: { receiverId, initialMessage: text },
        });
        setMessageText("");
      }
    }
  };

  const handleDeleteChannelClick = async () => {
    if (!activeChat?.id) return;
    const confirmDelete = window.confirm("¿Eliminar este canal? Esta acción no se puede deshacer.");
    if (!confirmDelete) return;

    const action = await dispatch(deleteChannel(activeChat.id));
    if (deleteChannel.fulfilled.match(action)) {
      setShowAddUsers(false);
      dispatch(fetchChannels());
      navigate("/chats", { replace: true });
    }
  };

  const handleSubmitChannelSettings = async (event) => {
    event.preventDefault();
    if (!activeChat?.id) return;

    const name = String(channelSettings.name || "").trim();
    const topic = String(channelSettings.topic || "").trim();

    if (!name) {
      setChannelSettingsError("El nombre es obligatorio.");
      return;
    }

    setChannelSettingsError(null);
    setSavingChannelSettings(true);

    const action = await dispatch(
      updateChannel({ channelId: activeChat.id, channelData: { name, topic } })
    );
    setSavingChannelSettings(false);

    if (updateChannel.fulfilled.match(action)) {
      await dispatch(fetchChannels());
      setShowAddUsers(false);
      return;
    }

    const err = action.payload || "No se pudo actualizar el canal.";
    setChannelSettingsError(String(err));
  };

  const handleAttachClick = () => {
    if (!fileInputRef.current) return;
    if (!chatIdKey) {
      alert("Primero envía un mensaje de texto para crear el chat.");
      return;
    }
    fileInputRef.current.click();
  };

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!chatIdKey || !currentUser || !token) {
      event.target.value = "";
      return;
    }

    const formData = new FormData();
    formData.append("chat_id", chatIdKey);
    formData.append("file", file);
    if (!isGroupChat && receiverId) formData.append("receiver_id", String(receiverId));

    setUploadingFile(true);
    try {
      const response = await fetch(`${backendBase}/chats/attachments/`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });

      const data = await response.json().catch(() => null);

      if (!response.ok) {
        console.error("Error al subir archivo:", data);
        alert(data?.detail || "No se pudo subir el archivo.");
        return;
      }

      dispatch(fetchChatHistory(chatIdKey));
    } catch (error) {
      console.error("Error al subir archivo:", error);
      alert("Error al subir archivo.");
    } finally {
      setUploadingFile(false);
      event.target.value = "";
    }
  };

  const loadMoreHistory = async () => {
    if (!nextCursor || loading) return;
    const container = chatContainerRef.current;
    const previousScrollHeight = container?.scrollHeight || 0;

    await dispatch(fetchMoreChatHistory(Number(chatIdKey)));

    requestAnimationFrame(() => {
      if (!container) return;
      const diff = container.scrollHeight - previousScrollHeight;
      container.scrollTop = diff + container.scrollTop;
    });
  };

  const getSenderInfo = (msg) => {
    const senderId = msg.sender?.id ?? msg.sender_id;
    const senderKey = String(senderId ?? "");

    if (senderKey && senderKey === String(currentUser?.id ?? "")) {
      return { isMine: true, user: currentUser };
    }

    const userObj =
      (senderKey ? chatUsersById.get(senderKey) : null) || msg.sender || null;

    return { isMine: false, user: userObj };
  };

  const openPreview = async (att) => {
    const directUrl = att.file_url || getMediaUrl(att.file);
    if (!directUrl) return;

    const type = att.file_type || "other";
    const label = att.original_filename || "archivo";

    if (previewAttachment?.url?.startsWith("blob:")) {
      URL.revokeObjectURL(previewAttachment.url);
    }

    const looksLikePdf =
      type === "document" &&
      (label.toLowerCase().endsWith(".pdf") || directUrl.toLowerCase().includes(".pdf"));

    if (!looksLikePdf) {
      setPreviewAttachment({ id: att.id, url: directUrl, type, label });
      return;
    }

    try {
      const response = await fetch(directUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : undefined,
      });

      if (!response.ok) {
        window.open(directUrl, "_blank", "noopener,noreferrer");
        return;
      }

      const downloadedBlob = await response.blob();
      const pdfBlob =
        downloadedBlob.type === "application/pdf"
          ? downloadedBlob
          : new Blob([downloadedBlob], { type: "application/pdf" });

      const blobUrl = URL.createObjectURL(pdfBlob);

      setPreviewAttachment({
        id: att.id,
        url: blobUrl,
        type: "document",
        label,
      });
    } catch {
      window.open(directUrl, "_blank", "noopener,noreferrer");
    }
  };

  const closePreview = () => {
    if (previewAttachment?.url?.startsWith("blob:")) {
      URL.revokeObjectURL(previewAttachment.url);
    }
    setPreviewAttachment(null);
  };

  const renderMessageText = (text) => {
    if (!text) return null;
    const parts = text.split(/(\@[A-Za-z0-9_.-]+)/g);
    return parts.map((part, index) => {
      if (/^\@[A-Za-z0-9_.-]+$/.test(part)) {
        return (
          <span key={index} className="chat-mention">
            {part}
          </span>
        );
      }
      return <span key={index}>{part}</span>;
    });
  };

  const renderAttachments = (msg) => {
    if (!Array.isArray(msg.attachments) || msg.attachments.length === 0) return null;

    return (
      <div className="message-attachments">
        {msg.attachments.map((att) => {
          const url = att.file_url || getMediaUrl(att.file);
          if (!url) return null;

          const type = att.file_type || "other";
          const label = att.original_filename || "archivo";

          if (type === "image" || type === "video") {
            const isImage = type === "image";
            return (
              <div key={att.id} className="message-attachment thumb">
                <div className="attachment-thumb" onClick={() => openPreview(att)}>
                  {isImage ? (
                    <img src={url} alt={label} className="attachment-thumb-media" />
                  ) : (
                    <video src={url} className="attachment-thumb-media" muted playsInline />
                  )}
                  <div className="attachment-overlay">
                    <Eye className="attachment-overlay-icon" />
                  </div>
                </div>
              </div>
            );
          }

          return (
            <div
              key={att.id}
              className="message-attachment file-chip"
              onClick={() => openPreview(att)}
            >
              <div className="file-chip-icon">
                <FileText size={18} />
              </div>
              <div className="file-chip-text">
                <span className="file-chip-name">{label}</span>
                <span className="file-chip-hint">Click para ver / descargar</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const handleChangeMessageText = (e) => {
    const value = e.target.value;
    setMessageText(value);

    if (!socketRef.current || !isSocketOpen || !currentUser || !chatIdKey) return;

    try {
      const payload = {
        kind: "typing",
        chat_id: chatIdKey,
        sender_id: currentUser.id,
        is_typing: true,
      };
      if (!isGroupChat && receiverId) payload.receiver_id = receiverId;
      socketRef.current.send(JSON.stringify(payload));
    } catch (error) {
      console.error("Error enviando estado typing:", error);
    }

    if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);

    typingTimeoutRef.current = setTimeout(() => {
      if (!socketRef.current || !isSocketOpen || !currentUser || !chatIdKey) return;

      try {
        const payload = {
          kind: "typing",
          chat_id: chatIdKey,
          sender_id: currentUser.id,
          is_typing: false,
        };
        if (!isGroupChat && receiverId) payload.receiver_id = receiverId;
        socketRef.current.send(JSON.stringify(payload));
      } catch (error) {
        console.error("Error enviando stop typing:", error);
      }
    }, 1500);
  };

  return (
    <div className="chatroom-container">
      <div className="chat-header">
        <div className="chat-header-row chat-header-row-between">
          <div className="chat-header-main">
            <div className="chat-header-avatar" aria-hidden="true">
              {headerAvatar.type === "user" && headerAvatar.avatarUrl ? (
                <img
                  src={headerAvatar.avatarUrl}
                  alt={headerAvatar.label}
                  className="avatar-img"
                />
              ) : (
                <div className="avatar-fallback">{headerAvatar.initials}</div>
              )}
            </div>

            <div className="chat-header-texts">
              <h2 className="chat-header-title">{headerTitle}</h2>
              {headerSubtitle ? <p className="chat-header-sub">{headerSubtitle}</p> : null}

              {isGroupChat && groupParticipants.length > 0 && (
                <div className="chat-group-participants">
                  {groupParticipants.map((user) => (
                    <div key={user.id} className="chat-group-participant-chip">
                      <div className="chip-avatar" aria-hidden="true">
                        {user.initial}
                      </div>
                      <span className="chip-name">{user.label}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {isGroupChat && canManageChannels && (
            <div className="chat-header-actions">
              <button
                type="button"
                className="chat-header-settings-btn"
                onClick={() => setShowAddUsers(true)}
                aria-label="Gestionar canal"
                title="Gestionar canal"
              >
                <Settings size={16} />
              </button>
            </div>
          )}
        </div>
      </div>

      {isGroupChat && canManageChannels && showAddUsers && (
        <div
          className="chat-modal-backdrop chat-modal-backdrop--blur"
          onClick={() => setShowAddUsers(false)}
          role="presentation"
        >
          <div
            className="chat-modal manage-users-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Gestionar canal"
          >
            <button
              type="button"
              className="chat-modal-close"
              onClick={() => setShowAddUsers(false)}
              aria-label="Cerrar"
              title="Cerrar"
            >
              ✕
            </button>

            <div className="chat-modal-header">
              <div>
                <h3 className="chat-modal-title">Gestionar canal</h3>
                <p className="chat-modal-subtitle">Edita el canal y gestiona usuarios desde aquí.</p>
              </div>
            </div>

            <form className="manage-users-form" onSubmit={handleSubmitChannelSettings}>
              <div className="chat-modal-body">
                <div className="manage-users-section">
                  <h4 className="manage-users-section-title">Canal</h4>

                  <div className="manage-channel-form">
                    <div className="manage-channel-field">
                      <label>Nombre</label>
                      <input
                        type="text"
                        value={channelSettings.name}
                        onChange={(e) =>
                          setChannelSettings((prev) => ({ ...prev, name: e.target.value }))
                        }
                        placeholder="Ej: recepción"
                        disabled={savingChannelSettings}
                      />
                    </div>

                    <div className="manage-channel-field">
                      <label>Tema</label>
                      <input
                        type="text"
                        value={channelSettings.topic}
                        onChange={(e) =>
                          setChannelSettings((prev) => ({ ...prev, topic: e.target.value }))
                        }
                        placeholder="Ej: avisos"
                        disabled={savingChannelSettings}
                      />
                    </div>

                    {channelSettingsError ? (
                      <div className="manage-channel-error">{channelSettingsError}</div>
                    ) : null}
                  </div>
                </div>

                <div className="manage-users-section">
                  <h4 className="manage-users-section-title">Miembros actuales</h4>

                  {groupParticipants.length === 0 ? (
                    <p className="chat-add-users-empty">Este canal no tiene miembros.</p>
                  ) : (
                    <ul className="chat-add-users-list">
                      {groupParticipants.map((participant) => {
                        const label = participant.label || "Usuario";
                        const avatarChar = (label[0] || "?").toUpperCase();
                        const canRemove = participant.id && participant.id !== currentUser?.id;

                        return (
                          <li key={participant.id} className="chat-add-users-item">
                            <div className="chat-add-users-info">
                              <div className="chat-add-users-avatar" aria-hidden="true">
                                {participant.avatarUrl ? (
                                  <img src={participant.avatarUrl} alt={label} className="avatar-img" />
                                ) : (
                                  <span>{avatarChar}</span>
                                )}
                              </div>

                              <div className="chat-add-users-texts">
                                <strong className="chat-add-users-name">{label}</strong>
                                <small className="chat-add-users-role">{participant.role || "-"}</small>
                              </div>
                            </div>

                            {canRemove ? (
                              <button
                                type="button"
                                className="manage-users-remove-btn"
                                onClick={() => handleRemoveUserFromChannel(participant.id)}
                              >
                                Quitar
                              </button>
                            ) : (
                              <span className="manage-users-fixed-tag">Tú</span>
                            )}
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>

                <div className="manage-users-section">
                  <h4 className="manage-users-section-title">Usuarios disponibles</h4>

                  {availableUsersToAdd.length === 0 ? (
                    <p className="chat-add-users-empty">No hay más usuarios para agregar.</p>
                  ) : (
                    <ul className="chat-add-users-list">
                      {availableUsersToAdd.map((user) => {
                        const label = getUserDisplayName(user);
                        const avatarUrl = user.profile_picture_url || user.profile_picture || null;
                        const avatarChar = (label[0] || "?").toUpperCase();

                        return (
                          <li key={user.id} className="chat-add-users-item">
                            <div className="chat-add-users-info">
                              <div className="chat-add-users-avatar" aria-hidden="true">
                                {avatarUrl ? (
                                  <img src={avatarUrl} alt={label} className="avatar-img" />
                                ) : (
                                  <span>{avatarChar}</span>
                                )}
                              </div>

                              <div className="chat-add-users-texts">
                                <strong className="chat-add-users-name">{label}</strong>
                                <small className="chat-add-users-role">{user.role || "-"}</small>
                              </div>
                            </div>

                            <button
                              type="button"
                              className="btn-primary btn-xs"
                              onClick={() => handleAddUserToChannel(user.id)}
                            >
                              Añadir
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              </div>

              <div className="manage-users-footer">
                <button
                  type="button"
                  className="btn-danger btn-sm"
                  disabled={savingChannelSettings}
                  onClick={handleDeleteChannelClick}
                >
                  Eliminar canal
                </button>

                <button type="submit" className="btn-primary btn-sm" disabled={savingChannelSettings}>
                  {savingChannelSettings ? "Guardando…" : "Guardar cambios"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {previewAttachment && (
        <div className="chat-modal-backdrop" onClick={closePreview}>
          <div className="chat-modal chat-preview-modal" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="chat-modal-close"
              onClick={closePreview}
              aria-label="Cerrar"
              title="Cerrar"
            >
              ✕
            </button>

            <div className="chat-modal-header">
              <div>
                <h3 className="chat-modal-title">{previewTitle}</h3>
              </div>
            </div>

            <div className="chat-modal-body chat-preview-body">
              {previewAttachment.type === "image" && (
                <img
                  src={previewAttachment.url}
                  alt="Vista previa"
                  className="chat-preview-image"
                />
              )}

              {previewAttachment.type === "video" && (
                <video src={previewAttachment.url} controls className="chat-preview-video" />
              )}

              {previewAttachment.type === "document" && (
                <iframe
                  src={previewAttachment.url}
                  title="Vista previa documento"
                  className="chat-preview-iframe"
                />
              )}

              {previewAttachment.type !== "image" &&
                previewAttachment.type !== "video" &&
                previewAttachment.type !== "document" && (
                  <div className="chat-preview-generic">
                    <p>No se puede previsualizar este tipo de archivo aquí.</p>
                  </div>
                )}
            </div>

            <div className="chat-modal-footer">
              <a href={previewAttachment.url} download={previewAttachment.label} className="ui-link">
                Descargar
              </a>
              <button type="button" className="btn-secondary btn-sm" onClick={closePreview}>
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="chat-messages" ref={chatContainerRef}>
        {chatIdKey && nextCursor && (
          <button className="load-more" disabled={loading} onClick={loadMoreHistory}>
            {loading ? "Cargando..." : "Cargar más mensajes"}
          </button>
        )}

        {messages.map((msg, idx) => {
          const { isMine, user } = getSenderInfo(msg);

          const avatarUrl = user?.profile_picture_url || user?.profile_picture || null;
          const displayName = getUserDisplayName(user);

          const initials = (() => {
            const [a = "", b = ""] = String(displayName).trim().split(/\s+/);
            const chars = (a[0] || "") + (b[0] || "");
            return (chars || "?").toUpperCase();
          })();

          return (
            <div
              key={msg.id || idx}
              className={`chat-message-row ${isMine ? "sent" : "received"}`}
            >
              {!isMine && (
                <div className="chat-message-avatar" aria-hidden="true">
                  {avatarUrl ? (
                    <img src={avatarUrl} alt={displayName} className="avatar-img" />
                  ) : (
                    <span>{initials}</span>
                  )}
                </div>
              )}

              <div className={`chat-message-bubble ${isMine ? "sent" : "received"}`}>
                <div className="message-content">
                  {msg.message && <p>{renderMessageText(msg.message)}</p>}
                  {renderAttachments(msg)}
                </div>
              </div>

              {isMine && (
                <div className="chat-message-avatar mine" aria-hidden="true">
                  {currentUser?.profile_picture_url || currentUser?.profile_picture ? (
                    <img
                      src={currentUser.profile_picture_url || currentUser.profile_picture}
                      alt={getUserDisplayName(currentUser)}
                      className="avatar-img"
                    />
                  ) : (
                    <span>{(getUserDisplayName(currentUser)[0] || "T").toUpperCase()}</span>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {typingUsers.length > 0 && (
          <div className="chat-typing-indicator">
            {typingUsers.length === 1
              ? `${getUserDisplayName(typingUsers[0])} está escribiendo…`
              : "Varios usuarios están escribiendo…"}
          </div>
        )}

        <div ref={endOfMessagesRef} />
      </div>

      <form
        className="chat-input"
        onSubmit={(e) => {
          e.preventDefault();
          sendMessage();
        }}
      >
        <button
          type="button"
          className="chat-attach-plus"
          onClick={handleAttachClick}
          disabled={uploadingFile || !chatIdKey}
          aria-label="Adjuntar archivo"
          title={!chatIdKey ? "Envía primero un mensaje para crear el chat" : "Adjuntar archivo"}
        >
          <Plus size={18} />
        </button>

        <input ref={fileInputRef} type="file" style={{ display: "none" }} onChange={handleFileChange} />

        <div className="chat-input-editor">
          <textarea
            ref={inputRef}
            rows={1}
            value={messageText}
            onChange={handleChangeMessageText}
            placeholder={messagePlaceholder}
            aria-label="Escribir mensaje"
            enterKeyHint="send"
            onKeyDown={(e) => {
              if (e.isComposing) return;
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
              }
            }}
          />

          <button
            type="submit"
            className="chat-send-inside"
            disabled={!messageText.trim() || (chatIdKey && !isSocketOpen)}
            aria-label="Enviar mensaje"
            title="Enviar"
          >
            <SendHorizontal size={18} />
          </button>
        </div>
      </form>
    </div>
  );
};

export default ChatRoom;